import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getAllCompanies, addCompanySecret, checkCompanySMTP } from '../services/api/implementation/impl';
import { useNavigate } from 'react-router-dom';
import Play from '../../shared/assets/svg/play.svg';
import toast from 'react-hot-toast';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Modal from '../../shared/components/ui/Modal';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';

const MailCompanies = () => {
    const userId = Cookies.get('userId');
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [smtpStatuses, setSmtpStatuses] = useState({});
    const { collapsed } = useSidebar();
    const [isConfigureModalOpen, setIsConfigureModalOpen] = useState(false);
    const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm();

    // Track page view
    useEffect(() => {
        mixpanel.track('Mail Companies Viewed', {
            user_id: userId,
            timestamp: new Date().toISOString()
        });
    }, [userId]);

    const { data: companiesData } = useQuery({
        queryKey: ['companies'],
        queryFn: getAllCompanies
    });

    useEffect(() => {
        if (companiesData) {
            setCompanies(companiesData);
            fetchSmtpStatuses(companiesData);
        }
    }, [companiesData]);

    const fetchSmtpStatuses = async (companiesList) => {
        const statuses = {};
        await Promise.all(
            companiesList.map(async (company) => {
                const status = await checkCompanySMTP(company.id);
                statuses[company.id] = !!status;
            })
        );
        setSmtpStatuses(statuses);
    };

    const { mutate: handleAddCompanySecret, isLoading: isSubmitting } = useMutation({
        mutationFn: (payload) => addCompanySecret(payload),
        onSuccess: () => {
            toast.success('Email settings configured successfully');
            setIsConfigureModalOpen(false);
            reset();
            // Refresh status
            checkCompanySMTP(selectedCompanyId).then((status) => {
                setSmtpStatuses((prev) => ({ ...prev, [selectedCompanyId]: !!status }));
            });
        },
        onError: (err) => {
            console.error('Error configuring email', err);
            toast.error(err?.response?.data?.message || err?.message || 'Error configuring email');
        }
    });

    const onConfigureClick = (companyId) => {
        setSelectedCompanyId(companyId);
        setIsConfigureModalOpen(true);
        // Reset form with default values
        reset({
            smtp_host: 'smtp.gmail.com',
            smtp_port: 465,
            smtp_secure: true,
            smtp_user: '',
            smtp_password: '',
            smtp_from_email: '',
            smtp_from_name: ''
        });
    };

    const onSubmit = (data) => {
        const payload = {
            ...data,
            company_id: selectedCompanyId,
            smtp_host: 'smtp.gmail.com',
            smtp_port: 465,
            smtp_secure: true
        };
        handleAddCompanySecret(payload);
    };

    return (
        <Layout>
            {/* Demo Modal */}
            {isDemoModalOpen && (
                <Modal
                    isDirty={true}
                    confirmOnClose={true}
                    name="Mail Integration Demo"
                    onClose={() => setIsDemoModalOpen(false)}
                    width={50}>
                    <iframe
                        width="100%"
                        height="315"
                        src="https://www.youtube.com/embed/zv0jXPmKBrk"
                        title="Mail Integration Demo"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen></iframe>
                </Modal>
            )}

            {/* Configure Modal */}
            {isConfigureModalOpen && (
                <Modal
                    name="Configure Email"
                    onClose={() => setIsConfigureModalOpen(false)}
                    width={40}
                    isDirty={true}>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-1 gap-4">
                            <FormInput
                                label="SMTP User (Gmail)"
                                register_key="smtp_user"
                                register={register}
                                errors={errors.smtp_user}
                                isRequired={true}
                                readOnly={isSubmitting}
                                width={100}
                            />
                            <FormInput
                                label="SMTP Password (App Password)"
                                register_key="smtp_password"
                                type="password"
                                register={register}
                                errors={errors.smtp_password}
                                isRequired={true}
                                readOnly={isSubmitting}
                                width={100}
                            />
                            <FormInput
                                label="From Email"
                                register_key="smtp_from_email"
                                register={register}
                                errors={errors.smtp_from_email}
                                isRequired={true}
                                readOnly={isSubmitting}
                                width={100}
                            />
                            <FormInput
                                label="From Name"
                                register_key="smtp_from_name"
                                register={register}
                                errors={errors.smtp_from_name}
                                isRequired={true}
                                readOnly={isSubmitting}
                                width={100}
                            />
                        </div>
                        <div className="flex justify-end gap-4 mt-6">
                            <Button type="button" displayType="secondary" onClick={() => setIsConfigureModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Connecting...' : 'Connect'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Header with Back Button */}
            <div
                className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
                    }`}>
                <Header
                    backArrow={true}
                    onBack={() => navigate('/builder/integration')}
                    data={{ name: 'Integrations > Email Setup' }}
                />
            </div>

            <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
                {/* Main Content */}
                <div className="mt-[4.5rem] w-full h-full bg-gray-100 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {companies.map((company) => (
                            <div key={company.id} className="bg-white shadow-md p-6 rounded-lg flex flex-col h-full">
                                <img className="border h-[6rem] object-contain mb-4" src={company.company_logo} alt="Company Logo" />
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="font-bold text-lg">{company.company_name}</h3>
                                    <div className="text-[#418b7a] flex gap-[2px] justify-center items-center text-sm font-medium ">
                                        <span className="border bg-[#418b7a] rounded-full p-[0.20rem] text-[#418b7a] mt-[2px]"></span>
                                        <p>Live</p>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 mb-4 text-justify flex-grow">
                                    Easily integrate with Email to sync data and streamline processes. Whether
                                    it&apos;s campaigns, notifications, or other tools, our integration ensures smooth connectivity.
                                </p>
                                <div className="w-full flex gap-4 justify-between mt-auto">
                                    <Button displayType="secondary" onClick={() => setIsDemoModalOpen(true)}>
                                        <span className="flex gap-1 justify-start items-center">
                                            <img src={Play} alt="" className="pt-1" />
                                            Demo
                                        </span>
                                    </Button>
                                    <Button
                                        onClick={() => onConfigureClick(company.id)}
                                        disabled={smtpStatuses[company.id]}
                                    >
                                        {smtpStatuses[company.id] ? 'Connected' : 'Configure'}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default MailCompanies;
